<template>
  <ReportCard :title="$t('report.members_acquisition_title')">
    <div class="flex items-center justify-end mb-2">
      <TogglePill
        :model-value="cumulative"
        @update:model-value="cumulative = $event"
      >
        <template #icon>
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 12 L5 8 L8 9 L12 3" />
          </svg>
        </template>
        {{ $t('report.members_cumulative') }}
      </TogglePill>
    </div>
    <div class="relative">
      <div ref="chartEl" class="w-full" style="height: 240px"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>
</template>

<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { MembersStatsResponse } from '../../responses'
import { trimToPresent, computeProjection } from '../../utils/Projection'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import TogglePill from '../ui/TogglePill.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: MembersStatsResponse | null
  loading: boolean
  formatXLabel: (dateString: string) => string
}>()

const { t } = useI18n()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const cumulative = ref(false)

const isDark = useIsDarkMode()

function buildChart() {
  if (!props.response?.intervals) return
  const dark = isDark.value
  const CYAN = '#06C8FF'
  const isCumulative = cumulative.value

  const intervals = props.response.intervals
  const rawData = intervals.map(i => i.data.reduce((sum, d) => sum + (d.metrics.count ?? 0), 0))
  const xAxisData = intervals.map(i => props.formatXLabel(i.date))

  // Build cumulative arrays first
  const currentCumulative: number[] = []
  let cumAcc = 0
  for (const v of rawData) { cumAcc += v; currentCumulative.push(cumAcc) }

  const cutoff = isCumulative
    ? trimToPresent(currentCumulative)
    : trimToPresent(rawData)

  let currentData: (number | null)[]
  if (isCumulative) {
    currentData = currentCumulative.map((v, i) => i < cutoff ? v : null)
  } else {
    currentData = rawData.map((v, i) => i < cutoff ? v : null)
  }

  let n1Cumulative: number[] | undefined
  const prev = props.response.previous_intervals
  if (prev?.length) {
    n1Cumulative = []
    let prevAcc = 0
    for (const iv of prev) {
      prevAcc += iv.data.reduce((sum, d) => sum + (d.metrics.count ?? 0), 0)
      n1Cumulative.push(prevAcc)
    }
  }

  const projection = computeProjection({ currentCumulative, n1Cumulative })

  const series: echarts.SeriesOption[] = []

  if (isCumulative) {
    series.push({
      type: 'line', name: t('report.members_acquisition_title'),
      data: currentData, smooth: true,
      lineStyle: { width: 3, color: CYAN },
      itemStyle: { color: CYAN },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: dark ? 'rgba(6, 200, 255, 0.12)' : 'rgba(6, 200, 255, 0.10)' },
          { offset: 1, color: 'rgba(6, 200, 255, 0.01)' },
        ]),
      },
      symbol: 'none', showSymbol: false, z: 10,
    })

    if (projection) {
      series.push({
        type: 'line', name: 'Projection',
        data: projection.projected, smooth: true,
        lineStyle: { width: 2, color: CYAN, type: 'dashed', opacity: 0.6 },
        itemStyle: { color: CYAN, opacity: 0.6 },
        symbol: 'none', showSymbol: false, z: 8,
      })
    }
  } else {
    series.push({
      type: 'bar', name: t('report.members_acquisition_title'),
      data: currentData,
      itemStyle: { color: CYAN, borderRadius: [3, 3, 0, 0] },
      barMaxWidth: 24,
      z: 10,
    })
  }

  // Previous period comparison
  if (prev?.length) {
    let prevData = prev.map(i => i.data.reduce((sum, d) => sum + (d.metrics.count ?? 0), 0))
    if (isCumulative) {
      prevData = n1Cumulative!
      series.push({
        type: 'line', name: t('report.members_acquisition_title') + ' (N-1)',
        data: prevData, smooth: true,
        lineStyle: { width: 2, color: dark ? 'rgba(255,255,255,0.25)' : 'rgba(100,116,139,0.35)', type: 'dashed' },
        itemStyle: { color: dark ? 'rgba(255,255,255,0.25)' : 'rgba(100,116,139,0.35)' },
        symbol: 'none', showSymbol: false, z: 6,
      })
    } else {
      series.push({
        type: 'bar', name: t('report.members_acquisition_title') + ' (N-1)',
        data: prevData,
        itemStyle: { color: dark ? 'rgba(255,255,255,0.15)' : 'rgba(100,116,139,0.25)', borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 24,
        z: 6,
      })
    }
  }

  const options: echarts.EChartsOption = {
    grid: { left: '2%', right: '4%', bottom: '12%', top: '6%', containLabel: true },
    xAxis: {
      type: 'category', data: xAxisData,
      axisLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(156,163,175,0.25)' } },
      axisTick: { show: false },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.7)', fontSize: 10 },
    },
    yAxis: {
      type: 'value', minInterval: 1,
      axisLine: { show: false }, axisTick: { show: false }, splitNumber: 4,
      splitLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.06)' : 'rgba(229,231,235,0.3)', type: 'dashed' } },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.6)', fontSize: 10, formatter: (v: number) => String(Math.round(v)) },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: dark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.96)',
      borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.6)',
      borderWidth: 1, borderRadius: 10, confine: true,
      padding: [8, 12],
      textStyle: { color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        let html = `<div style="font-weight:700;margin-bottom:6px;font-size:11px;opacity:0.6">${(params[0] as { axisValue: string }).axisValue}</div>`
        for (const p of params) {
          const param = p as { marker: string; seriesName: string; value: number | null }
          if (param.value != null) {
            html += `<div style="display:flex;align-items:center;gap:6px;margin-top:3px">${param.marker}<span style="font-weight:600">${param.seriesName}</span><span style="margin-left:auto;font-weight:700;font-variant-numeric:tabular-nums">${param.value.toLocaleString()}</span></div>`
          }
        }
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series,
  }

  if (chartInstance.value) {
    chartInstance.value.setOption(options, true)
  } else if (chartEl.value) {
    chartInstance.value = echarts.init(chartEl.value)
    chartInstance.value.setOption(options)
  }
}

function refreshChart() {
  buildChart()
}

watch(cumulative, () => buildChart())
watch(isDark, () => buildChart())

watch(() => props.response, async () => {
  await nextTick()
  buildChart()
})

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(buildChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
